import { useState } from 'react';
import { Plus, Trash2, Target } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, SectionLabel, Input, Select, Btn, Badge, StatCard } from '../components/UI';

const STATUS_OPTIONS = ['sent', 'replied', 'closed', 'skip'];
const STATUS_COLORS = { sent: 'amber', replied: 'blue', closed: 'accent', skip: 'text' };
const STATUS_LABELS = { sent: 'Sent', replied: 'Replied', closed: 'Closed 💰', skip: 'Skipped' };

export default function TrackerPage() {
  const { pitches, addPitch, updatePitch, deletePitch, stats } = useApp();
  const [form, setForm] = useState({ project: '', platform: 'X DM', script: 'A', amount: '', notes: '' });
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = () => {
    if (!form.project.trim()) return;
    addPitch({ project: form.project, platform: form.platform, script: form.script, amount: form.amount, notes: form.notes });
    setForm({ project: '', platform: 'X DM', script: 'A', amount: '', notes: '' });
    setShowForm(false);
  };

  const filtered = filter === 'all' ? pitches : pitches.filter(p => p.status === filter);
  const progress = Math.min(100, Math.round((stats.total / 20) * 100));

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>Pitch Tracker</h1>
          <p style={{ color: 'var(--text2)', fontSize: 14 }}>Track every pitch. Goal: 20 sent tonight.</p>
        </div>
        <Btn onClick={() => setShowForm(s => !s)}>
          <Plus size={15} /> Log pitch
        </Btn>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        <StatCard label="Total sent" value={stats.total} />
        <StatCard label="Replied" value={stats.replied} color="var(--blue)" />
        <StatCard label="Closed" value={stats.closed} color="var(--accent)" sub={`$${stats.earned} USDT earned`} />
        <StatCard label="Reply rate" value={`${stats.replyRate}%`} color={stats.replyRate > 20 ? 'var(--accent)' : 'var(--amber)'} />
      </div>

      {/* Progress bar */}
      <Card style={{ marginBottom: 20, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
          <span style={{ color: 'var(--text2)' }}>Tonight's goal</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{stats.total} / 20 pitches</span>
        </div>
        <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3 }}>
          <div style={{ height: 6, width: `${progress}%`, background: progress >= 100 ? 'var(--accent)' : 'var(--amber)', borderRadius: 3, transition: 'width 0.4s ease' }} />
        </div>
        {progress >= 100 && <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>🎯 Goal reached! Keep going.</div>}
      </Card>

      {/* Add pitch form */}
      {showForm && (
        <Card style={{ marginBottom: 20, border: '1px solid var(--accent)' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, color: 'var(--accent)' }}>Log new pitch</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: 5 }}>Project / handle *</label>
              <Input placeholder="@projectname" value={form.project} onChange={e => set('project', e.target.value)} style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: 5 }}>Platform</label>
              <Select value={form.platform} onChange={e => set('platform', e.target.value)} style={{ width: '100%' }}>
                {['X DM', 'X Reply', 'Discord', 'Telegram', 'Email'].map(p => <option key={p}>{p}</option>)}
              </Select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: 5 }}>Script used</label>
              <Select value={form.script} onChange={e => set('script', e.target.value)} style={{ width: '100%' }}>
                {['A','B','C','D','E','AI'].map(s => <option key={s} value={s}>Script {s}</option>)}
              </Select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: 5 }}>Deal amount ($USDT)</label>
              <Input placeholder="e.g. 80" value={form.amount} onChange={e => set('amount', e.target.value)} style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: 5 }}>Notes (optional)</label>
              <Input placeholder="anything useful..." value={form.notes} onChange={e => set('notes', e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={handleAdd}><Plus size={14} /> Add pitch</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['all', ...STATUS_OPTIONS].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
            fontFamily: 'var(--font-mono)',
            background: filter === f ? 'var(--accent-dim)' : 'transparent',
            color: filter === f ? 'var(--accent)' : 'var(--text2)',
            border: filter === f ? '1px solid var(--accent)' : '1px solid var(--border)',
            transition: 'all 0.15s',
          }}>
            {f === 'all' ? `All (${pitches.length})` : `${STATUS_LABELS[f]} (${pitches.filter(p => p.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem', gap: 12 }}>
            <Target size={36} color="var(--text3)" />
            <div style={{ fontSize: 14, color: 'var(--text3)' }}>
              {pitches.length === 0 ? 'Log your first pitch to start tracking' : 'No pitches match this filter'}
            </div>
          </div>
        </Card>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['#', 'Project', 'Platform', 'Script', 'Amount', 'Status', 'Time', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text3)', textAlign: 'left', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{i + 1}</td>
                  <td style={{ padding: '10px 14px', fontSize: 14, fontWeight: 500 }}>{p.project}</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text2)' }}>{p.platform}</td>
                  <td style={{ padding: '10px 14px' }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)' }}>Script {p.script}</span></td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: p.amount ? 'var(--accent)' : 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{p.amount ? `$${p.amount}` : '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <select
                      value={p.status}
                      onChange={e => updatePitch(p.id, { status: e.target.value })}
                      style={{
                        fontSize: 12, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                        fontFamily: 'var(--font-mono)', border: '1px solid var(--border2)',
                        background: 'var(--bg3)', color: 'var(--text)',
                      }}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                    {new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => deletePitch(p.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 4 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                    ><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
